import type { ReactElement } from 'react';
import { Modal, type ModalAction } from './Modal';
import { useUpdateStore, type InstallPhase } from '../store/updateStore';

const PHASE_LABEL: Record<InstallPhase, string> = {
  downloading: 'Downloading…',
  installing: 'Installing…',
  restarting: 'Restarting…',
};

/**
 * The blocking modal shown during the download, install, and relaunch
 * cycle. Visible whenever the store is in `installing` or `error` state.
 * While `installing`: no close affordance, Esc suppressed (handled by
 * passing a no-op `onClose` plus `closeOnBackdrop={false}`).
 * On `error`: a Close button reveals; clicking it returns the store to
 * `available` so the user can retry from Settings or the toast.
 */
export function UpdateInstallModal(): ReactElement | null {
  const status = useUpdateStore((s) => s.status);
  const latestVersion = useUpdateStore((s) => s.latestVersion);
  const installPhase = useUpdateStore((s) => s.installPhase);
  const error = useUpdateStore((s) => s.error);
  if (status !== 'installing' && status !== 'error') return null;
  const inFlight = status === 'installing';
  const phaseText = installPhase
    ? PHASE_LABEL[installPhase]
    : PHASE_LABEL.downloading;
  const onCloseError = (): void => {
    useUpdateStore.setState({ status: 'available', error: null, installPhase: null });
  };
  // While installing there is no dismiss affordance; on error a single Close
  // button (which is also the Esc/Enter target) returns the store to `available`.
  const actions: ModalAction[] | undefined = inFlight
    ? undefined
    : [{ id: 'close', label: 'Close', onSelect: onCloseError, accelerator: 'c', isCancel: true, isDefault: true }];
  return (
    <Modal
      open
      onClose={inFlight ? () => {} : onCloseError}
      closeOnBackdrop={!inFlight}
      title={`Updating to v${latestVersion ?? '?'}`}
      actions={actions}
    >
      <div className="mp-update-install">
        {inFlight ? (
          <>
            <div className="mp-update-install__spinner" aria-hidden />
            <p className="mp-update-install__phase">{phaseText}</p>
            <p className="mp-update-install__sub">
              Slate will restart automatically.
            </p>
          </>
        ) : (
          <p className="mp-modal__text">{error ?? 'Update failed.'}</p>
        )}
      </div>
    </Modal>
  );
}
